import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { glossarioTerms, categorias } from '../data/glossario';
import { useState } from 'react';

const SITE_NAME = import.meta.env.VITE_SITE_NAME || 'Cripto News';
const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://criptonews.site';

export const GlossarioPage: React.FC = () => {
  const [filtro, setFiltro] = useState('');
  const [categoriaAtiva, setCategoriaAtiva] = useState('Todas');

  const termosFiltrados = glossarioTerms.filter((t) => {
    const matchTexto =
      filtro === '' ||
      t.termo.toLowerCase().includes(filtro.toLowerCase()) ||
      t.resumo.toLowerCase().includes(filtro.toLowerCase());
    const matchCategoria = categoriaAtiva === 'Todas' || t.categoria === categoriaAtiva;
    return matchTexto && matchCategoria;
  });

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'DefinedTermSet',
    name: 'Glossário de Criptomoedas',
    description: 'Dicionário completo de termos do mercado cripto em português.',
    url: `${SITE_URL}/glossario`,
    inLanguage: 'pt-BR',
  };

  return (
    <>
      <Helmet>
        <title>Glossário de Criptomoedas em Português | {SITE_NAME}</title>
        <meta
          name="description"
          content="Dicionário completo de termos do mercado cripto em português. Entenda Bitcoin, Ethereum, DeFi, NFT, Halving, Staking e muito mais com explicações claras para brasileiros."
        />
        <link rel="canonical" href={`${SITE_URL}/glossario`} />
        <meta property="og:title" content="Glossário de Criptomoedas em Português" />
        <meta property="og:description" content="Dicionário completo de termos cripto em PT-BR. Bitcoin, DeFi, NFT, Staking e muito mais explicados de forma clara." />
        <meta property="og:url" content={`${SITE_URL}/glossario`} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Hero */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-12">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <h1 className="text-4xl font-bold mb-3">Glossário de Criptomoedas</h1>
            <p className="text-blue-100 text-lg mb-6">
              {glossarioTerms.length} termos explicados em português — do básico ao avançado
            </p>
            {/* Search */}
            <input
              type="text"
              placeholder="Buscar termo... (ex: Bitcoin, DeFi, Staking)"
              value={filtro}
              onChange={(e) => setFiltro(e.target.value)}
              className="w-full max-w-xl px-5 py-3 rounded-xl text-gray-900 text-base focus:outline-none focus:ring-2 focus:ring-yellow-400"
            />
          </div>
        </div>

        <div className="container mx-auto px-4 max-w-4xl py-8">
          {/* Category Filter */}
          <div className="flex flex-wrap gap-2 mb-8">
            {['Todas', ...categorias].map((cat) => (
              <button
                key={cat}
                onClick={() => setCategoriaAtiva(cat)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition ${
                  categoriaAtiva === cat
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-600 border border-gray-200 hover:border-blue-300'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Results count */}
          <p className="text-gray-500 text-sm mb-6">
            {termosFiltrados.length} {termosFiltrados.length === 1 ? 'termo encontrado' : 'termos encontrados'}
          </p>

          {/* Terms Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {termosFiltrados.map((termo) => (
              <Link
                key={termo.slug}
                to={`/glossario/${termo.slug}`}
                className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 hover:shadow-md hover:border-blue-200 transition group"
              >
                <div className="flex items-start justify-between mb-2">
                  <h2 className="text-lg font-bold text-gray-900 group-hover:text-blue-600 transition">
                    {termo.termo}
                  </h2>
                  <span className="text-xs bg-blue-50 text-blue-600 px-2 py-1 rounded-full ml-2 whitespace-nowrap">
                    {termo.categoria}
                  </span>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">{termo.resumo}</p>
                <span className="mt-3 inline-flex items-center text-blue-600 text-sm font-medium group-hover:gap-2 transition-all">
                  Ler definição completa →
                </span>
              </Link>
            ))}
          </div>

          {termosFiltrados.length === 0 && (
            <div className="text-center py-16 text-gray-500">
              <p className="text-xl mb-2">Nenhum termo encontrado</p>
              <p className="text-sm">Tente outro termo de busca</p>
            </div>
          )}

          {/* SEO Footer */}
          <div className="mt-12 bg-blue-50 rounded-xl p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-3">Por que aprender sobre criptomoedas?</h2>
            <p className="text-gray-700 text-sm leading-relaxed mb-3">
              O mercado de criptomoedas cresce rapidamente no Brasil — segundo a Receita Federal, mais de 1,4 milhão de brasileiros declararam criptoativos no IR em 2023. Entender os termos básicos é o primeiro passo para participar com segurança desse mercado.
            </p>
            <p className="text-gray-700 text-sm leading-relaxed">
              Este glossário foi criado especialmente para o investidor brasileiro, com exemplos práticos e contexto local. Todos os termos são atualizados regularmente pela equipe do {SITE_NAME}.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};
